import { OnlyViewHoc } from 'hocs'
import * as React from 'react'

import { AppButton } from 'components/AppButton'
import { AppModalHeader } from 'components/AppModal/AppModalHeader'
import { TextArea } from 'components/TextArea'

import { Application, ApplicationStatus } from 'models/application'
import { HistoryStatusObject } from 'models/status'

import { DocumentsList, StatusHistory } from 'shared/ui'

import styles from './ApplicationDetails.module.scss'

interface Props {
  application: Application
  disabled: boolean
  onAccept(): void
  onRework(controllerComment: string): void
  onRefuse(): void
  onClose?(): void
  openLivePhoto(): void
  openAttachments(index: number): void
  setLightbox(index: Array<string>): void
  attachments: string[]
  statusHistory: HistoryStatusObject[] | null
}

export const ApplicationDetails: React.FC<Props> = ({
  application,
  disabled,
  attachments,
  onRework,
  onAccept,
  onClose,
  openAttachments,
  setLightbox,
  statusHistory,
}) => {
  const [controllerComment, setControllerComment] = React.useState(
    application.controller_comment || '',
  )
  function onDowloadPhotos() {
    const fullName = `${application.name}_${application.surname}`

    if (application.client_photo)
      downloadImage(application.client_photo, `${fullName}_клиент`)
    if (application.anketa_photo)
      downloadImage(application.anketa_photo, `${fullName}_анкета`)
    if (application.passport_front_photo)
      downloadImage(
        application.passport_front_photo,
        `${fullName}_паспрот_лицевая`,
      )
    if (application.passport_back_photo)
      downloadImage(
        application.passport_back_photo,
        `${fullName}_паспрот_задняя`,
      )
  }

  const {
    client_photo: clientPhoto,
    anketa_photo: anketaPhoto,
    children,
  } = application
  const passportPhotos = React.useMemo(
    () =>
      [
        application.passport_front_photo,
        application.passport_back_photo,
      ].filter(Boolean),
    [application],
  )
  const childrenPhotos = React.useMemo(
    () =>
      [
        application.children?.child_certificate,
        application.children?.child_certificate_back,
      ].filter(Boolean) as string[],
    [application],
  )

  return (
    <>
      <div className={styles.container}>
        <AppModalHeader onClose={onClose} title={`Заявка #${application.id}`} />
        <div className={styles.content}>
          <div className={styles.item}>
            <div className={styles.itemName}>Фамилия</div>
            <div className={styles.itemText}>{application.surname || '-'}</div>
          </div>
          <div className={styles.item}>
            <div className={styles.itemName}>Имя</div>
            <div className={styles.itemText}>{application.name || '-'}</div>
          </div>
          <div className={styles.item}>
            <div className={styles.itemName}>Отчество</div>
            <div className={styles.itemText}>
              {application.middle_name || '-'}
            </div>
          </div>

          <div className={styles.item}>
            <div className={styles.itemName}>ФИО в АБС</div>
            <div className={styles.itemText}>{application.name || '-'}</div>
          </div>
          <div className={styles.item}>
            <div className={styles.itemName}>ИНН</div>
            <div className={styles.itemText}>{application.inn || '-'}</div>
          </div>
          {application?.card?.number && (
            <div className={styles.item}>
              <div className={styles.itemName}>Банковская карта</div>
              <div className={styles.cardContainer}>
                <div className={styles.card}>{application.card?.type}</div>
                <span>{application?.card?.number}</span>
              </div>
            </div>
          )}
          {application.m_junior && children && (
            <div className={styles.item}>
              <div className={styles.itemName}>Ребенок</div>
              <div className={styles.itemText}>
                {children.name} {children.surname} - {children.phone_number}
              </div>
            </div>
          )}

          {!application.m_junior && attachments.length ? (
            <div className={styles.item}>
              <div className={styles.itemName}>Фото анкеты</div>
              <div className={styles.photos}>
                {attachments.map((item, index) => {
                  return (
                    <div
                      key={item}
                      onClick={() => openAttachments(index)}
                      className={styles.itemPhoto}
                    >
                      <img src={item} alt="" />
                    </div>
                  )
                })}
              </div>
            </div>
          ) : null}

          {statusHistory && <StatusHistory list={statusHistory} />}

          {application.m_junior && (
            <>
              {anketaPhoto && (
                <div className={styles.item}>
                  <div className={styles.itemName}>Фото анкеты</div>
                  <div className={styles.photos}>
                    <div
                      key={anketaPhoto}
                      onClick={() => setLightbox([anketaPhoto])}
                      className={styles.itemPhoto}
                    >
                      <img src={anketaPhoto} alt="" />
                    </div>
                  </div>
                </div>
              )}

              {passportPhotos.length > 0 && (
                <div className={styles.item}>
                  <div className={styles.itemName}>Фото паспорта</div>
                  <div className={styles.photos}>
                    {passportPhotos.map((item, index) => {
                      return (
                        <div
                          key={item}
                          onClick={() => setLightbox(passportPhotos)}
                          className={styles.itemPhoto}
                        >
                          <img src={item} alt="" />
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {clientPhoto && (
                <div className={styles.item}>
                  <div className={styles.itemName}>Фото родителя с картой</div>
                  <div className={styles.photos}>
                    <div
                      key={clientPhoto}
                      onClick={() => setLightbox([clientPhoto])}
                      className={styles.itemPhoto}
                    >
                      <img src={clientPhoto} alt="" />
                    </div>
                  </div>
                </div>
              )}

              {childrenPhotos.length > 0 && (
                <div className={styles.item}>
                  <div className={styles.itemName}>
                    Фото свидетельства ребёнка
                  </div>
                  <div className={styles.photos}>
                    {childrenPhotos.map((item, index) => {
                      return (
                        <div
                          key={item}
                          onClick={() => setLightbox(childrenPhotos)}
                          className={styles.itemPhoto}
                        >
                          <img src={item} alt="" />
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          <AppButton onClick={onDowloadPhotos} size="small" variant="siren">
            Скачать
          </AppButton>

          {application.anketa_file && (
            <a href={application.anketa_file} target="_blank" rel="noreferrer">
              <AppButton variant="gray" size="small">
                Анкета
              </AppButton>
            </a>
          )}

          <OnlyViewHoc>
            {[
              ApplicationStatus.INITED,
              ApplicationStatus.CONSIDERATION,
            ].includes(application.status) && (
              <AppButton
                disabled={disabled}
                onClick={() => onRework(controllerComment)}
                size="small"
                variant="yellow"
              >
                На доработку
              </AppButton>
            )}

            {[ApplicationStatus.CONSIDERATION].includes(application.status) ? (
              <>
                <div className={styles.buttons}>
                  <AppButton
                    disabled={disabled}
                    onClick={onAccept}
                    size="small"
                    variant="siren"
                  >
                    Принять
                  </AppButton>
                </div>
                <TextArea
                  rows={4}
                  placeholder="Комментарий"
                  value={controllerComment}
                  onChange={e => {
                    const rawValue = e.currentTarget.value
                    setControllerComment(rawValue.slice(0, 3000))
                  }}
                />
              </>
            ) : null}
          </OnlyViewHoc>

          <DocumentsList list={application.tyndyk_passport} />
        </div>
      </div>
    </>
  )
}

async function downloadImage(
  imageSrc: string,
  imageName: string = 'imageName',
) {
  const image = await fetch(imageSrc)
  const imageBlog = await image.blob()
  const imageURL = URL.createObjectURL(imageBlog)

  const link = document.createElement('a')
  link.href = imageURL
  link.download = imageName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
